import { Injectable, Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Lookup } from '../helpers/lookup';
import { ApiService } from './api.service';
import { LotService } from './lot.service';

import { CommunityDataLevel, ICommunity, ILot, ILotMapStatus, ISetting } from '@ml/common';

/*
  Dream Finders Homes acquired Beazer Homes. Merged Beazer communities keep their Beazer data (map artwork, lots)
  but must look exactly like native Dream Finders communities. This service swaps in the Dream Finders settings and
  lot status naming/colors right after the community is loaded, before anything reads it.

  Add ?beazerTheme=true to a URL to bypass the swap and see the community in the original Beazer theme.
*/

// Beazer communities that are NOT part of the merger and keep the Beazer theme. Final list TBD before cutover.
const EXCLUDED_COMMUNITY_IDS = [42005]; // Acacia Foothills -- test entry to verify exclusion works

// Environments (by Argo domain) the merge runs in. Any environment not listed, including production, keeps the
// Beazer theme. settingsSourceCommunityId is the Dream Finders community whose settings and lot status colors are used
// as the Dream Finders theme.
const ENVIRONMENTS: Record<string, { enabled: boolean; settingsSourceCommunityId: number }> = {
  'argo.ml3ds-rt-stage.com': { enabled: true, settingsSourceCommunityId: 193167 }, // Tampa > Beazer Placeholder
  'argo.ml3ds-uat-stage.com': { enabled: true, settingsSourceCommunityId: 45176 }, // Antigua (Jacksonville)
  'argo.ml3ds-cloud.com': { enabled: false, settingsSourceCommunityId: null } // production
};

// Settings (by Name) that describe the Beazer community rather than the brand, so the Beazer value is kept
const PRESERVED_SETTINGS: string[] = [];

const BYPASS_QUERY_PARAM = 'beazerTheme';

// Beazer statuses that don't exist for Dream Finders. Lots using them move to Sold.
const STATUSES_MAPPED_TO_SOLD = ['under contract', 'occupied'];
const SOLD_STATUS_NAME = 'Sold';
// Beazer status that doesn't exist for Dream Finders. Lots using it are left with no status so they show blank.
const REMOVED_STATUS_NAME = 'unavailable';

@Injectable({
  providedIn: 'root'
})
export class DreamFindersMergeService {
  private active = false;
  // NeighborhoodId -> (Beazer status id -> the status id lots should use instead, null = no status)
  private statusIdRemaps = new Map<number, Map<number, number>>();

  constructor(
    private api: ApiService,
    private lookup: Lookup,
    // LotService depends on CommunityService, which depends on this service, so it is resolved lazily
    private injector: Injector
  ) {}

  get isActive() {
    return this.active;
  }

  isMergedCommunity(community: ICommunity): boolean {
    return (
      !!community &&
      community.ClientId === this.lookup.ClientIds.Beazer &&
      !EXCLUDED_COMMUNITY_IDS.includes(community.CommunityId) &&
      !!this.getEnvironment()?.enabled &&
      !this.isBypassed()
    );
  }

  // Swaps the Dream Finders theme into the community in place. Falls back to the Beazer theme if the
  // Dream Finders community cannot be loaded so the app still renders.
  async apply(community: ICommunity, dataLevel: CommunityDataLevel): Promise<ICommunity> {
    if (this.active) this.injector.get(LotService).setLotTransform(null);
    this.active = false;
    this.statusIdRemaps.clear();

    if (!this.isMergedCommunity(community)) return community;

    try {
      const dreamFindersCommunity = await this.fetchDreamFindersCommunity(dataLevel);

      community.SettingsCategories = this.mergeSettings(community, dreamFindersCommunity);
      this.mergeLotStatuses(community, dreamFindersCommunity);

      this.active = true;
      // Lots loaded after the community (refreshes, single lot fetches, saves) need the same status remap
      this.injector.get(LotService).setLotTransform(lot => this.remapLotStatus(lot));
    } catch (error) {
      console.error('Unable to apply Dream Finders theme. Using Beazer theme.', error);
    }

    return community;
  }

  mergeSettings(beazerCommunity: ICommunity, dreamFindersCommunity: ICommunity) {
    const merged = structuredClone(dreamFindersCommunity.SettingsCategories);
    if (!PRESERVED_SETTINGS.length) return merged;

    const beazerSettings = beazerCommunity.SettingsCategories.flatMap(c => c.Settings);
    merged.forEach(category => {
      category.Settings = category.Settings.map(
        (setting: ISetting) =>
          (PRESERVED_SETTINGS.includes(setting.Name) &&
            beazerSettings.find(s => s.Name === setting.Name)) ||
          setting
      );
    });

    return merged;
  }

  mergeLotStatuses(beazerCommunity: ICommunity, dreamFindersCommunity: ICommunity) {
    const dreamFindersStatuses = dreamFindersCommunity.Neighborhoods.flatMap(n => n.LotMapStatuses);

    beazerCommunity.Neighborhoods.forEach(neighborhood => {
      const remap = new Map<number, number>();
      this.statusIdRemaps.set(neighborhood.NeighborhoodId, remap);
      neighborhood.LotMapStatuses = this.remapStatuses(neighborhood.LotMapStatuses, remap);

      neighborhood.LotMapStatuses.forEach(status => {
        const match = findByName(dreamFindersStatuses, status.Name);
        if (match) {
          status.ColorHexValue = match.ColorHexValue;
          status.FocusColorHexValue = match.FocusColorHexValue;
        } else console.warn(`No Dream Finders lot status found matching "${status.Name}"`);
      });

      neighborhood.Lots?.forEach(lot => this.remapLotStatus(lot));
    });
  }

  private remapLotStatus(lot: ILot): ILot {
    const remap = this.statusIdRemaps.get(lot.NeighborhoodId);
    if (remap?.has(lot.StatusId)) lot.StatusId = remap.get(lot.StatusId);
    return lot;
  }

  // Removes Beazer only statuses from the list and records which status id their lots should use instead
  private remapStatuses(statuses: ILotMapStatus[], remap: Map<number, number>): ILotMapStatus[] {
    const toSold = statuses.filter(s => STATUSES_MAPPED_TO_SOLD.includes(normalize(s.Name)));
    const toRemove = statuses.filter(s => normalize(s.Name) === REMOVED_STATUS_NAME);

    let soldStatus = findByName(statuses, SOLD_STATUS_NAME);
    if (!soldStatus && toSold.length) {
      // no Sold status in this neighborhood, so the first one becomes Sold
      soldStatus = toSold.shift();
      soldStatus.Name = SOLD_STATUS_NAME;
    }

    toSold.forEach(s => remap.set(s.LotMapStatusId, soldStatus.LotMapStatusId));
    toRemove.forEach(s => remap.set(s.LotMapStatusId, null));

    return statuses.filter(s => !toSold.includes(s) && !toRemove.includes(s));
  }

  private async fetchDreamFindersCommunity(dataLevel: CommunityDataLevel): Promise<ICommunity> {
    const sourceCommunityId = this.getEnvironment()?.settingsSourceCommunityId;
    if (!sourceCommunityId)
      throw new Error('No Dream Finders settings source community for this environment');

    return firstValueFrom(this.api.getCommunityById(sourceCommunityId, dataLevel));
  }

  private getEnvironment() {
    return ENVIRONMENTS[environment.apiDomain] ?? null;
  }

  private isBypassed(): boolean {
    const value = new URLSearchParams(window.location.search).get(BYPASS_QUERY_PARAM);
    return value?.toLowerCase() === 'true';
  }
}

function normalize(name: string): string {
  return name?.trim().toLowerCase() ?? '';
}

function findByName(statuses: ILotMapStatus[], name: string): ILotMapStatus {
  return statuses.find(s => normalize(s.Name) === normalize(name));
}
